import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiLoader, FiArrowLeft, FiFileText, FiCpu, FiExternalLink } from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';
import { SiIndeed } from 'react-icons/si';
import { toast } from 'react-toastify';
import { jobApi } from '../../services/api';
import ExtensionBanner from '../../components/ExtensionBanner';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useCache } from '../../contexts/CacheContext';

const JobCreation = () => {
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    sourceUrl: '',
    summary: '',
    responsibilities: [],
    requirements: [],
    skills: [],
    location: '',
    employment_type: '',
    salary_range: ''
  });
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [errors, setErrors] = useState({});
  const [parsedSuccessfully, setParsedSuccessfully] = useState(false);
  
  const navigate = useNavigate();
  const { invalidateJobCache } = useCache();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }
    
    if (formData.sourceUrl && !isValidUrl(formData.sourceUrl)) {
      newErrors.sourceUrl = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };
  
  const handleArrayItemChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    
    setFormData({
      ...formData,
      [field]: newArray
    });
  };
  
  const handleAddArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };
  
  const handleRemoveArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    
    setFormData({
      ...formData,
      [field]: newArray
    });
  };
  
  const handleParseDescription = async () => {
    if (!formData.description.trim()) {
      setErrors({
        ...errors,
        description: 'Job description is required for parsing'
      });
      return;
    }
    
    try {
      setParsing(true);
      
      const response = await jobApi.parseJobDescription(formData.description);
      
      // Update form with parsed data
      setFormData({
        ...formData,
        title: response.data.title || formData.title,
        company: response.data.company || formData.company,
        summary: response.data.summary || '',
        responsibilities: response.data.responsibilities || [],
        requirements: response.data.requirements || [],
        skills: response.data.skills || [],
        location: response.data.location || '',
        employment_type: response.data.employment_type || ''
      });
      
      setParsedSuccessfully(true);
      toast.success('Job description parsed successfully!');
    } catch (error) {
      console.error('Error parsing job description:', error);
      toast.error('Failed to parse job description. Please try again or fill in the fields manually.');
    } finally {
      setParsing(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setLoading(true);
      const response = await jobApi.createJob(formData);
      
      // Invalidate the job cache so the job list will be refreshed
      invalidateJobCache();
      
      toast.success('Job opportunity added successfully!');
      // Redirect to jobs page with the newly added job ID as a highlight parameter
      navigate(`/jobs/saved?highlight=${response.data.id}`);
    } catch (error) {
      console.error('Error creating job:', error);
      toast.error(error.response?.data?.detail || 'Failed to create job. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const copy = pageDescriptions.jobCreation;

  return (
    <div className="max-w-3xl mx-auto">
      <ExtensionBanner />
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <button
            type="button"
            aria-label="Back to Saved Jobs"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            onClick={() => navigate('/jobs/saved')}
          >
            <FiArrowLeft className="mr-1 h-4 w-4" />
            Back to Saved Jobs
          </button>
        }
      />
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            Add New Job Opportunity
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Paste the job description and we'll automatically extract key information, or fill in the fields manually.
          </p>
        </div>
        
        <div className="border-t border-gray-200">
          <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6">
            {/* Quick links and instructions */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3">Find a job to add:</p>
              <div className="flex flex-wrap gap-3 mb-3">
                <a
                  href="https://www.linkedin.com/jobs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-800 hover:bg-[#f3f6f8] hover:border-[#0a66c2] transition-colors shadow-sm"
                >
                  <FaLinkedin className="h-6 w-6 text-[#0a66c2]" />
                  LinkedIn Jobs
                  <FiExternalLink className="h-4 w-4 text-gray-400" />
                </a>
                <a
                  href="https://www.indeed.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-800 hover:bg-[#f5f5f5] hover:border-[#2164f3] transition-colors shadow-sm"
                >
                  <SiIndeed className="h-6 w-6 text-[#2164f3]" />
                  Indeed
                  <FiExternalLink className="h-4 w-4 text-gray-400" />
                </a>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Instructions:</span> Open a job listing, select all the job description text (Ctrl+A or Cmd+A on the page), copy it, then paste it into the Job Description field below. Our AI will extract key details automatically.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              {/* Job Description with AI parsing */}
              <div className="sm:col-span-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Job Description *
                </label>
                <div className="mt-1">
                  <textarea
                    id="description"
                    name="description"
                    rows={8}
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.description ? 'border-red-300' : ''
                    }`}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Paste the full job description here..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    aria-label="Auto-extract information from job description"
                    onClick={handleParseDescription}
                    disabled={parsing}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {parsing ? (
                      <>
                        <FiLoader className="animate-spin mr-2 h-4 w-4" />
                        Parsing...
                      </>
                    ) : (
                      <>
                        <FiCpu className="mr-2 h-4 w-4" />
                        Auto-Extract Information
                      </>
                    )}
                  </button>
                </div>
                {parsedSuccessfully && (
                  <div className="mt-2 text-sm text-green-600">
                    <FiFileText className="inline mr-1" />
                    Data extracted successfully! Review and edit the fields below.
                  </div>
                )}
              </div>
              
              {/* Job Title */}
              <div className="sm:col-span-3">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Job Title *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.title ? 'border-red-300' : ''
                    }`}
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Software Engineer"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
              </div>
              
              {/* Company */}
              <div className="sm:col-span-3">
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.company ? 'border-red-300' : ''
                    }`}
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="e.g., Acme Inc."
                  />
                  {errors.company && (
                    <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                  )}
                </div>
              </div>
              
              {/* Location */}
              <div className="sm:col-span-3">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="location"
                    id="location"
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., New York, NY (Remote)"
                  />
                </div>
              </div>
              
              {/* Employment Type */}
              <div className="sm:col-span-3">
                <label htmlFor="employment_type" className="block text-sm font-medium text-gray-700">
                  Employment Type
                </label>
                <div className="mt-1">
                  <select
                    id="employment_type"
                    name="employment_type"
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.employment_type}
                    onChange={handleChange}
                  >
                    <option value="">Select Type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>
              
              {/* Source URL */}
              <div className="sm:col-span-6">
                <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700">
                  Source URL
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="sourceUrl"
                    id="sourceUrl"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.sourceUrl ? 'border-red-300' : ''
                    }`}
                    value={formData.sourceUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/job-posting"
                  />
                  {errors.sourceUrl ? (
                    <p className="mt-1 text-sm text-red-600">{errors.sourceUrl}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      The URL where you found this job posting (optional)
                    </p>
                  )}
                </div>
              </div>
              
              {/* Summary */}
              <div className="sm:col-span-6">
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700">
                  Job Summary
                </label>
                <div className="mt-1">
                  <textarea
                    id="summary"
                    name="summary"
                    rows={3}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.summary}
                    onChange={handleChange}
                    placeholder="Brief overview of the position..."
                  />
                </div>
              </div>
              
              {/* Skills */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Required Skills
                </label>
                {formData.skills.map((skill, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={skill}
                      onChange={(e) => handleArrayItemChange('skills', index, e.target.value)}
                      placeholder="e.g., JavaScript, Project Management"
                    />
                    <button
                      type="button"
                      aria-label={`Remove skill ${index + 1}`}
                      onClick={() => handleRemoveArrayItem('skills', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  aria-label="Add skill"
                  onClick={() => handleAddArrayItem('skills')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Skill
                </button>
              </div>

              <div className="sm:col-span-3">

                {/* Add Salary Range */}
              <label htmlFor="salary_range" className="block text-sm font-medium text-gray-700">
                Salary Range
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="salary_range"
                  name="salary_range"
                  className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  value={formData.salary_range || ''}
                  onChange={(e) => handleChange(e)}
                  placeholder="e.g., $80,000-$100,000/year"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Add the salary range if available
              </p>
            </div>
              
              {/* Responsibilities */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Responsibilities
                </label>
                {formData.responsibilities.map((resp, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={resp}
                      onChange={(e) => handleArrayItemChange('responsibilities', index, e.target.value)}
                      placeholder="Enter job responsibility"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('responsibilities', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem('responsibilities')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Responsibility
                </button>
              </div>
              
              {/* Requirements */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Requirements
                </label>
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={req}
                      onChange={(e) => handleArrayItemChange('requirements', index, e.target.value)}
                      placeholder="Enter job requirement"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('requirements', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem('requirements')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Requirement
                </button>
              </div>
            </div>
            
            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  aria-label="Cancel and return to jobs list"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => navigate('/jobs')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  aria-label="Create job"
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="-ml-1 mr-2 h-4 w-4" />
                      Save Job
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobCreation;